/* global JSX */
import { Alert, Box, Chip, Stack, Typography } from "@mui/material";
import * as React from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult, UserGroupType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { IModifyUser, ModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { AppUtils } from "../../../framework/appUtils";
import { ToolTitle } from "../../framework/Tool/ToolTitle";
import { AddUserToolButton } from "source/components/framework/Button/AddUserToolButton";
import { RemoveUserToolButton } from "source/components/framework/Button/RemoveUserToolButton";
import { SelectUserGroupDialog } from "../SelectUserGroupDialog/SelectUserGroupDialog";
import { IUserGroupListItem } from "ui/Scripts/source/models/userGroup/userGroupListItem";
import { UserGroupListItems } from "ui/Scripts/source/models/userGroup/userGroupListItems";

//UserGroupBlockItem
export interface IUserGroupBlockItemProp {
    userGroup: IModifyUser;
    userCode?: string;
    selectedCodes: string[];
    onClick: (code: string) => void;
}

export class UserGroupBlockItem extends React.Component<IUserGroupBlockItemProp, {}> {
    handleBadgeClick = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.onClick(this.props.userGroup.code);
        return 0;
    };

    render() {
        const props = this.props;
        const userGroup = props.userGroup;
        const selected = props.selectedCodes.indexOf(userGroup.code) > -1;

        return (
            <Chip
                label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography>{userGroup.name}</Typography>
                    </Box>
                }
                avatar={
                    <AvatarImage
                        id={userGroup.code}
                        marginLeft={"4px"}
                        name={userGroup.name}
                        image={userGroup.picture}
                        tooltip={userGroup.name}
                    />
                }
                variant={selected ? "filled" : "outlined"}
                disabled={userGroup.isRemoved()}
                onClick={(e) => {
                    e.stopPropagation();
                    props.onClick(userGroup.code);
                }}
            />
        );
    }
}

//UserGroupBlocks
export interface IUserGroupBlocksProp {
    classes?: string;
    companyId: string;
    userCode?: string;
    isReadOnly?: boolean;
    title: string | JSX.Element;
    selectionTitle: string;
    noUsersMessage?: string;
    parentIsNew?: boolean;
    parentIsNewMessage?: string;
    defaultUserGroupType?: UserGroupType;
    listModeChangeDisabled?: boolean;
    userGroups: IModifyUser[];
    removeUsersConfirmation?: string;
    margin?: string | number;
    onAddUserGroups: (users: IModifyUser[]) => Promise<ApiSuccess>;
    onRemoveUserGroups: (userCodes: string[]) => Promise<ApiSuccess>;
    onGetUserGroupSelectListItems?: (companyId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, groupType: number,
        ignoreUserGroupCodes: string[], requireUserGroupCodes: string[], ignoreQrCodeUserGroups: boolean, requireQrCodeUserGroups: boolean, messageTargetMode: boolean) => Promise<UserGroupListItems>;
}

interface IUserGroupBlocksState {
    selectedCodes: string[];
    showSelectUserDialog: boolean;
}

export class UserGroupBlocks extends React.Component<IUserGroupBlocksProp, IUserGroupBlocksState> {
    static defaultProps = {
        margin: "1rem 0 0 0"
    };

    constructor(props) {
        super(props);
        this.state = {
            selectedCodes: [],
            showSelectUserDialog: false
        };
    }

    componentDidUpdate(prevProps: IUserGroupBlocksProp, prevState: IUserGroupBlocksState) {
        const props = this.props;
        const state = this.state;
        const prevHash = JSON.stringify(prevProps.userGroups);
        const hash = JSON.stringify(props.userGroups);
        if (prevHash === hash) return;
        const userCodes = props.userGroups.map(i => i.code);
        this.setState({
            selectedCodes: state.selectedCodes.filter(i => userCodes.indexOf(i) > -1)
        });
    }

    handleBlockClick = (selectedCode: string) => {
        this.setState({
            selectedCodes: [selectedCode]
        });
    };

    // #region Add User
    handleAdd = () => {
        this.setState({
            showSelectUserDialog: true
        });
    };

    handleSelectUserGroupDialogOk = async (userGroups: IUserGroupListItem[], userGroupType: UserGroupType) => {
        const props = this.props;
        if (!userGroups || userGroups.length < 1) return;
        const newUserGroups = userGroups.map(user => {
            const newUser = new ModifyUser();
            newUser.code = user.code;
            newUser.name = user.name;
            newUser.picture = user.picture;
            newUser.date = userGroupType;
            return newUser;
        });
        // Call server
        const result = await AppUtils.callService(() => props.onAddUserGroups(newUserGroups));
        if (!result) return;
        this.setState({
            showSelectUserDialog: false
        });
    };

    handleSelectUserGroupDialogCancel = () => {
        this.setState({
            showSelectUserDialog: false
        });
    };
    // #endregion Add User

    // #region Remove User
    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.selectedCodes.length < 1) return;
        if (await AppUtils.showConfirmationDialog(props.removeUsersConfirmation ?? Translations.DoYouReallyWantToRemoveSelectedUsers, false) === ConfirmationDialogResult.Yes) {
            // Call server
            await AppUtils.callService(() => props.onRemoveUserGroups(state.selectedCodes));
        }
    };
    // #endregion Remove User

    render() {
        const props = this.props;
        const state = this.state;
        const userGroups = props.userGroups;
        const titleIsString = Base.isString(props.title);
        return (
            <>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ margin: props.margin }}>
                    {titleIsString &&
                        <ToolTitle
                            title={props.title}
                        />
                    }
                    {!titleIsString &&
                        props.title
                    }
                    <Stack direction="row">
                        {!props.isReadOnly &&
                            <AddUserToolButton
                                disabled={props.parentIsNew}
                                tooltip={Translations.Add}
                                onClick={this.handleAdd}
                            />
                        }
                        {!props.isReadOnly &&
                            <RemoveUserToolButton
                                tooltip={Translations.Remove}
                                disabled={state.selectedCodes.length < 1}
                                onClick={this.handleRemove}
                            />
                        }
                    </Stack>
                </Box>
                <Stack direction="row" spacing={1} sx={{ marginTop: ".5rem" }} useFlexGap flexWrap="wrap">
                    {!props.isReadOnly && !!props.parentIsNew && !!props.parentIsNewMessage &&
                        <Alert severity="info" sx={{ width: "100%" }}>{props.parentIsNewMessage}</Alert>
                    }
                    {!props.isReadOnly && userGroups.length < 1 && !!props.noUsersMessage &&
                        <Alert severity="info" sx={{ width: "100%" }}>{props.noUsersMessage}</Alert>
                    }
                    {userGroups.map((userGroup) =>
                        <UserGroupBlockItem
                            key={userGroup.code}
                            userCode={props.userCode}
                            userGroup={userGroup}
                            selectedCodes={state.selectedCodes}
                            onClick={this.handleBlockClick}
                        />
                    )}
                </Stack>
                {state.showSelectUserDialog &&
                    <SelectUserGroupDialog
                        companyId={props.companyId}
                        defaultUserGroupType={props.defaultUserGroupType}
                        title={props.selectionTitle}
                        multiSelect={true}
                        listModeChangeDisabled={props.listModeChangeDisabled}
                        ignoreUserGroupCodes={props.userGroups.map(i => i.code)}
                        onOk={this.handleSelectUserGroupDialogOk}
                        onCancel={this.handleSelectUserGroupDialogCancel}
                        onGetUserGroupListItems={props.onGetUserGroupSelectListItems}
                    />
                }
            </>
        );
    }
}